"use client";

import ProductLink from "@/components/navigation/ProductLink";
import { CurrencyCodeAmount } from "@/components/CurrencyCodeAmount";
import { useMemo } from "react";
import type { Product } from "@/types";
import type { ModelSummary, StationSummary } from "@/hooks/useStationHubData";
import { bikeLicenseText, getBikeClass } from "@/lib/mobility/bike-classes";
import { periodTotal } from "@/lib/mobility/pricing";
import { Button } from "@/components/ui/button";
import { pickLabel, useHub, useHubDensity } from "./hub-context";
import {
  HUB_GUTTER, Thumb,
  HubCell,
  hubListClass,
} from "./hub-ui";
import { StationRow } from "./StationRow";
import { SheetPageLink } from "./StationSheetParts";

/** 요금 그리드 칸 (spec §2-4 · §3-1). */
const PERIODS: Array<{ days: number; key: string }> = [
  { days: 1, key: "d1" },
  { days: 3, key: "d3" },
  { days: 7, key: "d7" },
];

/** 모델 시트에 보여줄 지점 수. */
const STATION_LIMIT = 12;

/**
 * 차종 시트(V0b) — 사진·클래스·면허 + 기간 요금 그리드 + 이 차종을 받을 수 있는 지점.
 * spec §3-1.
 */
export function ModelSheet({ model }: { model: ModelSummary }) {
  const {
    t,
    locale,
    data,
    open,
    rawDistanceKm,
    formatPrice,
    productHref,
    modelLink,
  } = useHub();
  // 모델 허브가 있는 차종만(10-15) — "{허브 이름} 전체 보기 ›"
  const hubLink = modelLink?.(model.key);
  const wide = useHubDensity() === "wide";

  const bikeClass = getBikeClass(model.classKey);
  const currency = model.currency ?? model.products[0]?.currency;

  // 기간별 총액 = 모델의 상품(인기/스탠다드) 중 최저.
  const prices = useMemo(
    () =>
      PERIODS.map(({ days, key }) => {
        const total = model.products.reduce((min, product) => {
          const value = periodTotal(product, days) ?? 0;
          return value > 0 && (min === 0 || value < min) ? value : min;
        }, 0);
        return { key, days, total };
      }),
    [model.products]
  );

  // 이 차종을 받을 수 있는 지점 — 가까운 순.
  const stations = useMemo(() => {
    const list = [...model.stationIds]
      .map((id) => data.stationsById.get(id))
      .filter((s): s is StationSummary => !!s)
      .map((station) => ({
        station,
        km: rawDistanceKm(station.lat, station.lng),
      }));
    list.sort((a, b) => a.km - b.km);
    return list.map((entry) => entry.station);
  }, [model.stationIds, data.stationsById, rawDistanceKm]);

  const nearest = stations[0];
  const nearestDetail = nearest ? data.details.get(nearest.id) : undefined;
  const nearestProduct: Product | undefined = nearest
    ? model.products.find((product) =>
        product.branches?.some((branch) => branch.id === nearest.id)
      ) ?? model.products[0]
    : undefined;
  // CTA 가격 = 가장 가까운 지점 상품의 1일가, 없으면 모델 최저가.
  const ctaPrice =
    (nearestProduct ? periodTotal(nearestProduct, 1) : null) ||
    model.minPrice ||
    0;

  return (
    <div className="pb-8">
      <div
        className={`flex items-start gap-3 pt-1 mapShow:gap-4 ${HUB_GUTTER}`}
      >
        <Thumb
          src={model.image}
          alt={model.name}
          w={wide ? 120 : 80}
          h={wide ? 90 : 80}
          rounded={wide ? "rounded-xl" : "rounded-lg"}
        />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[16px] font-bold text-gray-900">
            {model.name}
          </h2>
          {bikeClass ? (
            <p className="mt-1 text-[12px] text-gray-500">
              {pickLabel(bikeClass.head, locale)}
            </p>
          ) : null}
          {bikeClass ? (
            <p className="mt-0.5 text-[12px] text-gray-500">
              {t("hub.license")} · {bikeLicenseText(bikeClass.key, locale)}
            </p>
          ) : null}
          {hubLink ? (
            <SheetPageLink href={hubLink.path} className="mt-1.5 max-w-full truncate">
              {t("hub.modelHubLink", { name: hubLink.name })}
            </SheetPageLink>
          ) : null}
        </div>
      </div>

      {/* 요금 그리드 — 늘 3열, gap-px + 회색 바탕으로 1px 구분선만 남긴다.
          칸 글자 자리 = 그리드 폭/3 − 32px(px-4): 모바일 360 ≈ 88px · 390 ≈ 98px.
          그리드 폭 400px 미만이면 통화 코드를 숫자 위 한 줄로 올려 7자리 금액
          ("KRW 1,090,702")도 줄바꿈·넘침 없이 들어간다(컨테이너 쿼리 /mprice). */}
      <div className="@container/mprice mt-4 grid grid-cols-3 gap-px border-y border-gray-100 bg-gray-100">
        {prices.map(({ key, total }) => (
          <div key={key} className="min-w-0 bg-white px-4 py-3">
            <div className="text-[12px] text-gray-500">
              {t(`hub.priceHead.${key}`)}
            </div>
            <div
              className="mt-0.5 whitespace-nowrap text-[14px] font-semibold tabular-nums text-gray-900"
              suppressHydrationWarning
            >
              {total > 0 ? (
                <CurrencyCodeAmount
                  value={formatPrice(total, currency)}
                  codeClassName="block leading-none mb-0.5 @min-[400px]/mprice:inline @min-[400px]/mprice:mb-0"
                />
              ) : (
                "—"
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        className={`flex items-baseline justify-between gap-3 pt-4 pb-2 ${HUB_GUTTER}`}
      >
        <h3 className="text-[14px] font-semibold text-gray-900">
          {t("hub.modelStations")}
        </h3>
        <span className="shrink-0 text-[12px] text-gray-500">
          {t("hub.nearestFrom", { n: stations.length })}
        </span>
      </div>

      <ul
        className={hubListClass(
          wide,
          // 테두리·구분선 대신 여백으로 구분(09-29, 지점 목록과 같은 규칙)
          "flex flex-col gap-1",
          "grid grid-cols-2 items-start gap-x-3 gap-y-2",
        )}
      >
        {stations.slice(0, STATION_LIMIT).map((station) => (
          <li key={station.id}>
            <HubCell wide={wide} bare>
              <StationRow
                station={station}
                onClick={() => open({ kind: "bikes", stationId: station.id })}
              />
            </HubCell>
          </li>
        ))}
      </ul>

      {/* CTA 는 지점 이름이 있어야 문구가 완성된다 — 상세가 오기 전에는 띄우지 않는다
          (도시명으로 지점 이름을 대신 쓰지 않는다). */}
      {nearest && nearestProduct && nearestDetail?.name ? (
        <div className={`${HUB_GUTTER} pt-4`}>
          <Button
            asChild
            className="h-12 w-full rounded-xl bg-orange-500 text-base font-bold text-white hover:bg-orange-600"
          >
            <ProductLink
              href={productHref(nearestProduct.id, nearest.id)}
              suppressHydrationWarning
            >
              {t("hub.bookAt", {
                s: nearestDetail.name,
                p:
                  ctaPrice > 0
                    ? formatPrice(ctaPrice, nearestProduct.currency ?? currency)
                    : "—",
              })}
            </ProductLink>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
